import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, BookOpen, MessageSquare, Bell, Clock, CheckCircle, Loader } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { bookingService } from '../../services/api';

interface BookingStats {
  total: number;
  pending: number;
  confirmed: number;
  upcoming: Booking[];
}

interface Booking {
  id: number;
  code_reservation: string;
  date_debut: string;
  status: string;
  apartment?: { titre: string; ville: string };
}

const Dashboard: React.FC = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<BookingStats>({ total: 0, pending: 0, confirmed: 0, upcoming: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await bookingService.getMyBookings();
        const bookings = response.data.bookings || [];
        const pending = bookings.filter((b: Booking) => b.status === 'pending').length;
        const confirmed = bookings.filter((b: Booking) => b.status === 'confirmed').length;
        const upcoming = bookings
          .filter((b: Booking) => new Date(b.date_debut) >= new Date())
          .slice(0, 3);
        setStats({ total: bookings.length, pending, confirmed, upcoming });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const formatDate = (date: string) => new Date(date).toLocaleDateString('fr-FR', { 
    day: 'numeric', month: 'short' 
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-neutral-900">Bienvenue, {user?.prenom} !</h1>
        <p className="text-neutral-600">Gérez vos réservations et messages depuis votre espace.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/locataire/bookings" className="card p-6 hover:shadow-lg transition-shadow">
          <BookOpen className="w-8 h-8 text-primary-500 mb-3" />
          <h3 className="font-semibold">Mes réservations</h3>
          <p className="text-2xl font-bold text-primary-600 mt-1">
            {loading ? <Loader className="w-5 h-5 animate-spin" /> : stats.total}
          </p>
        </Link>
        
        <div className="card p-6">
          <Clock className="w-8 h-8 text-yellow-500 mb-3" />
          <h3 className="font-semibold">En attente</h3>
          <p className="text-2xl font-bold text-yellow-600 mt-1">
            {loading ? <Loader className="w-5 h-5 animate-spin" /> : stats.pending}
          </p>
        </div>
        
        <div className="card p-6">
          <CheckCircle className="w-8 h-8 text-green-500 mb-3" />
          <h3 className="font-semibold">Confirmées</h3>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {loading ? <Loader className="w-5 h-5 animate-spin" /> : stats.confirmed}
          </p>
        </div>
        
        <Link to="/apartments" className="card p-6 hover:shadow-lg transition-shadow">
          <Calendar className="w-8 h-8 text-secondary-500 mb-3" />
          <h3 className="font-semibold">Rechercher</h3>
          <p className="text-sm text-neutral-500">Trouver un appartement</p>
        </Link>
      </div>

      {/* Upcoming bookings */}
      {stats.upcoming.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4">Prochaines réservations</h2>
          <div className="space-y-3">
            {stats.upcoming.map((booking) => (
              <div key={booking.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                <div>
                  <span className="font-mono text-sm text-neutral-500">{booking.code_reservation}</span>
                  <p className="font-medium">{booking.apartment?.titre}</p>
                  <p className="text-sm text-neutral-500">{booking.apartment?.ville}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatDate(booking.date_debut)}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {booking.status === 'pending' ? 'En attente' : 'Confirmée'}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <Link to="/locataire/bookings" className="btn btn-outline w-full mt-4">
            Voir toutes les réservations
          </Link>
        </div>
      )}

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/locataire/messages" className="card p-6 hover:shadow-lg transition-shadow flex items-center gap-4">
          <MessageSquare className="w-10 h-10 text-secondary-500" />
          <div>
            <h3 className="font-semibold">Messages</h3>
            <p className="text-sm text-neutral-500">Consulter vos messages</p>
          </div>
        </Link>
        <Link to="/locataire/notifications" className="card p-6 hover:shadow-lg transition-shadow flex items-center gap-4">
          <Bell className="w-10 h-10 text-accent-500" />
          <div>
            <h3 className="font-semibold">Notifications</h3>
            <p className="text-sm text-neutral-500">Voir les notifications</p>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
